'use client'

import { facetListDoc } from '@vendure/hub/data-access'
import { Checkbox } from '@vendure/shadcn/ui'
import { cn } from '@vendure/shadcn/util'
import { Heading } from '@vendure/shared/ui'
import { ResultOf } from 'gql.tada'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

export interface FilterGroup {
  id: string
  label: string
  options: Array<{
    label: string
    value: string
    checked: boolean
  }>
}

const PaidTypeFilter: FilterGroup = {
  id: 'paid',
  label: 'Type',
  options: [
    { label: 'Free', value: 'free', checked: false },
    { label: 'Premium', value: 'paid', checked: false },
  ],
}

export const FilterMenu = ({
  categories,
  activeCategory,
}: {
  categories: ResultOf<typeof facetListDoc>['facets']['items'][0]['values']
  activeCategory?: string | null
}) => {
  const searchParams = useSearchParams()
  const { replace } = useRouter()
  const pathname = usePathname()
  const filterGroups = [PaidTypeFilter]

  function onFilterChange(group: string, values: string[] | string) {
    const params = new URLSearchParams(searchParams)
    params.set('page', '1')

    if (
      (typeof values === 'string' && values) ||
      (Array.isArray(values) && values.length > 0)
    ) {
      params.set(group, values.toString())
    } else {
      console.log('delete', group)
      params.delete(group)
    }
    replace(`${pathname}?${params.toString()}`)
  }

  const applyFilters = (group: string, option: string, checked: boolean) => {
    const checkedFilters = (searchParams?.get(group)?.split(',') ?? []).filter(
      (f) => !!f,
    )
    if (checked) {
      checkedFilters.push(option)
    } else {
      checkedFilters.splice(checkedFilters.indexOf(option), 1)
    }
    onFilterChange(group, checkedFilters.length ? checkedFilters : [])
  }
  return (
    <>
      <h3 className="sr-only">Categories</h3>
      <ul
        role="list"
        className="ml-3 mt-4 space-y-4 border-b border-white/10 pb-6 text-sm font-medium"
      >
        <li key="all">
          <button
            className={cn({
              'transition-color hover:text-white/70': true,
              'text-primary-500': !activeCategory,
              'text-white/60': !!activeCategory,
            })}
            onClick={() => {
              onFilterChange('category', [])
            }}
          >
            All
          </button>
        </li>
        {categories
          .slice(0)
          .sort((a, b) => (a.name < b.name ? -1 : 1))
          .map((category) => (
            <li key={category.name}>
              <button
                className={cn({
                  'transition-color hover:text-white/70': true,
                  'text-primary-500': activeCategory === category.code,
                  'text-white/60': activeCategory !== category.code,
                })}
                onClick={() => {
                  onFilterChange('category', category.code)
                }}
              >
                {category.name}
              </button>
            </li>
          ))}
      </ul>
      {filterGroups.map((group, idx) => (
        <div
          key={group.label}
          className="mx-3 mt-6"
        >
          <div className="-my-3 flow-root">
            <div className="flex w-full items-center justify-between py-3">
              <Heading as="span">{group.label}</Heading>
            </div>
          </div>
          <div className="pt-6">
            <div className="ml-2 space-y-4">
              {group.options.map((option, optionIdx) => (
                <div
                  className="flex items-center"
                  key={option.value}
                >
                  <Checkbox
                    id={`filter-${option.label}-${optionIdx}`}
                    name={`${option.label}[]`}
                    defaultChecked={searchParams
                      ?.getAll(group.id)
                      .includes(option.value)}
                    onCheckedChange={(checked) =>
                      applyFilters(group.id, option.value, checked === true)
                    }
                    className="dark:data-[state=checked]:bg-primary-500 dark:data-[state=checked]:text-primary-100 h-4  w-4 rounded"
                  />
                  <label
                    htmlFor={`filter-${option.label}-${optionIdx}`}
                    className="ml-3 text-sm text-white/60"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </>
  )
}
